import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Menu, X, Bell, User, LogOut } from 'lucide-react'
import { useUser as useReduxUser, useAppDispatch } from '@/store/hooks'
import { useAuthStore } from '@/store/authStore'
import { logoutUser } from '@/store/slices/authSlice'
import { sessionService } from '@/services/sessionService'
import WhatsAppBotLauncher from '@/components/whatsapp/WhatsAppBotLauncher'
import LanguageSwitcher from '@/components/common/LanguageSwitcher'
import { NfcRwaLogo } from '@/components/branding/NfcRwaLogo'
import { useTranslation } from '@/contexts/LanguageContext'

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [avatarError, setAvatarError] = useState(false)
  const reduxUser = useReduxUser()
  const zustandUser = useAuthStore((state) => state.user)
  const zustandLogout = useAuthStore((state) => state.logout)
  // Use Zustand user if available, fallback to Redux user
  const user = zustandUser || reduxUser
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Reset avatar error when profile picture changes
  useEffect(() => {
    setAvatarError(false);
  }, [user?.profile_picture])

  const handleLogout = async () => {
    try {
      // Get user role before clearing auth state
      const userRole = user?.role || reduxUser?.role
      
      // Clear both Redux and Zustand auth states
      await dispatch(logoutUser())
      zustandLogout()
      
      // Clear session
      sessionService.endSession()
      sessionService.clearSessionStorage()
      
      // Clear any additional localStorage items
      localStorage.removeItem('token')
      
      // Redirect based on user role
      let redirectPath = '/user-login' // Default for staff, department_head, front_worker, etc.
      
      if (userRole === 'universal_admin' || userRole === 'office_admin') {
        redirectPath = '/nfcrwacontrol'
      } else if (userRole === 'member') {
        redirectPath = '/login'
      }
      
      navigate(redirectPath, { replace: true })
    } catch (error) {
      console.error('Logout error:', error)
      // Even if there's an error, still logout and redirect
      const userRole = user?.role || reduxUser?.role
      zustandLogout()
      
      // Redirect based on user role
      let redirectPath = '/user-login' // Default
      if (userRole === 'universal_admin' || userRole === 'office_admin') {
        redirectPath = '/nfcrwacontrol'
      } else if (userRole === 'member') {
        redirectPath = '/login'
      }
      
      navigate(redirectPath, { replace: true })
    }
  }

  // Base navigation - visible to all authenticated users
  const baseNavigation = [
    { name: t('navigation.dashboard'), href: '/app/dashboard', icon: '📊' },
    { name: t('navigation.complaints'), href: '/app/complaints', icon: '📝' },
    { name: t('navigation.profile'), href: '/app/profile', icon: '👤' },
  ]

  // Member-only navigation
  const memberNavigation = [
    { name: t('navigation.events'), href: '/app/events', icon: '📅' },
    { name: t('navigation.business'), href: '/app/business', icon: '🏢' },
    { name: t('navigation.charges'), href: '/app/charges', icon: '💰' },
    { name: t('navigation.elections'), href: '/app/elections', icon: '🗳️' },
  ]

  // Department Head & Front Worker navigation
  const departmentStaffNavigation = [
    { name: 'My Tasks', href: '/app/complaints?view=tasks', icon: '✅' },
    { name: 'Pending Tasks', href: '/app/complaints?status=pending&view=tasks', icon: '⏳' },
    { name: 'Completed Tasks', href: '/app/complaints?status=resolved&view=tasks', icon: '✓' },
  ]

  // Office Admin navigation
  const officeAdminNavigation = [
    { name: t('navigation.members'), href: '/app/admin/members', icon: '👥' },
    { name: 'All Complaints', href: '/app/complaints', icon: '📝' },
    { name: 'Register Complaint', href: '/app/complaints/new', icon: '➕' },
    { name: t('navigation.departments'), href: '/app/admin/departments', icon: '🏢' },
    { name: 'Department Representatives', href: '/app/admin/department-heads', icon: '👤' },
    { name: 'Committee Members', href: '/app/admin/committee-members', icon: '👨‍💼' },
    { name: 'Notifications', href: '/app/admin/cms/notifications', icon: '🔔' },
    { name: 'Press Release', href: '/app/admin/cms/press-releases', icon: '📰' },
    { name: t('navigation.reports'), href: '/app/reports', icon: '📈' },
  ]

  // Universal Admin navigation
  const universalAdminNavigation = [
    { name: t('navigation.members'), href: '/app/admin/members', icon: '👥' },
    { name: 'Committee Members', href: '/app/admin/committee-members', icon: '👨‍💼' },
    { name: 'Department Representatives', href: '/app/admin/department-heads', icon: '👤' },
    { name: 'Notifications', href: '/app/admin/cms/notifications', icon: '🔔' },
    { name: 'Press Release', href: '/app/admin/cms/press-releases', icon: '📰' },
    { name: t('navigation.staff'), href: '/app/admin/users', icon: '👨‍💼' },
    { name: t('navigation.departments'), href: '/app/admin/departments', icon: '🏢' },
    { name: t('navigation.categories'), href: '/app/admin/categories', icon: '📂' },
    { name: t('navigation.cms'), href: '/app/admin/cms', icon: '📄' },
    { name: t('navigation.reports'), href: '/app/reports', icon: '📈' },
  ]

  // Committee Member navigation (multi-layer access)
  const committeeMemberNavigation = [
    { name: 'My Complaints', href: '/app/complaints?view=my-complaints', icon: '📋' },
    { name: 'Department Complaints', href: '/app/complaints?view=department', icon: '🏢' },
    { name: 'Member View', href: '/app/complaints?view=member', icon: '👤' },
  ]

  // Role checks
  const isUniversalAdmin = user?.role === 'universal_admin'
  const isOfficeAdmin = user?.role === 'office_admin'
  const isAdmin = isUniversalAdmin || isOfficeAdmin
  const isDepartmentHead = user?.role === 'department_head'
  const isFrontWorker = user?.role === 'front_worker'
  const isDepartmentStaff = isDepartmentHead || isFrontWorker
  const isMember = user?.role === 'member'
  const isCommitteeMember = user?.role === 'committee_member'
  const isCommitteeMemberWithRoles = isCommitteeMember && (user?.is_committee_member || user?.department_id)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white" data-testid="mobile-menu">
          <div className="flex h-16 items-center justify-between px-4 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <NfcRwaLogo className="h-9 w-9 shrink-0" />
              <h1 className="text-xl font-bold text-gray-900 truncate">NFCRWA</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 shrink-0"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {/* Base Navigation - All Users */}
            {baseNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={item.name.toLowerCase() === 'complaints' ? 'complaints-link' : `mobile-${item.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.name}
                </Link>
              )
            })}

            {/* Member Navigation */}
            {isMember && memberNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.name}
                </Link>
              )
            })}

            {/* Department Staff Navigation */}
            {isDepartmentStaff && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tasks
                </div>
                {departmentStaffNavigation.map((item) => {
                  const isActive = location.pathname === item.href || location.search.includes(item.href.split('?')[1])
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Committee Member Navigation */}
            {isCommitteeMemberWithRoles && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Committee
                </div>
                {committeeMemberNavigation.map((item) => {
                  const isActive = location.pathname === item.href || location.search.includes(item.href.split('?')[1])
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Office Admin Navigation */}
            {isOfficeAdmin && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Office Admin
                </div>
                {officeAdminNavigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Universal Admin Navigation */}
            {isUniversalAdmin && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Admin
                </div>
                {universalAdminNavigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4 gap-2">
            <NfcRwaLogo className="h-9 w-9 shrink-0" />
            <h1 className="text-xl font-bold text-gray-900">NFCRWA</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {/* Base Navigation - All Users */}
            {baseNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={item.name.toLowerCase() === 'complaints' ? 'complaints-link' : `desktop-${item.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.name}
                </Link>
              )
            })}

            {/* Member Navigation */}
            {isMember && memberNavigation.map((item) => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-blue-100 text-blue-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.name}
                </Link>
              )
            })}

            {/* Department Staff Navigation */}
            {isDepartmentStaff && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tasks
                </div>
                {departmentStaffNavigation.map((item) => {
                  const isActive = location.pathname === item.href || location.search.includes(item.href.split('?')[1])
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Committee Member Navigation */}
            {isCommitteeMemberWithRoles && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Committee
                </div>
                {committeeMemberNavigation.map((item) => {
                  const isActive = location.pathname === item.href || location.search.includes(item.href.split('?')[1])
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Office Admin Navigation */}
            {isOfficeAdmin && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Office Admin
                </div>
                {officeAdminNavigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}

            {/* Universal Admin Navigation */}
            {isUniversalAdmin && (
              <>
                <div className="border-t border-gray-200 my-4" />
                <div className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Admin
                </div>
                {universalAdminNavigation.map((item) => {
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                        isActive
                          ? 'bg-blue-100 text-blue-900'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <span className="mr-3 text-lg">{item.icon}</span>
                      {item.name}
                    </Link>
                  )
                })}
              </>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <LanguageSwitcher variant="compact" />
              
              <button
                type="button"
                className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
              >
                <Bell className="h-6 w-6" />
              </button>

              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />

              <div className="relative">
                <div className="flex items-center gap-x-4" data-testid="user-menu">
                  <div className="hidden lg:block">
                    <div className="text-sm font-medium text-gray-900">
                      {user?.first_name} {user?.last_name}
                    </div>
                    <div className="text-xs text-gray-500">{user?.role}</div>
                  </div>
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {(() => {
                      const profilePic = user?.profile_picture;
                      const hasProfilePic = profilePic && profilePic.trim() !== '' && profilePic !== 'null' && profilePic !== 'undefined';
                      
                      if (hasProfilePic && !avatarError) {
                        return (
                          <img 
                            src={profilePic} 
                            alt={`${user.first_name} ${user.last_name}`}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              console.warn('Failed to load profile picture:', profilePic, 'Error:', e);
                              setAvatarError(true);
                            }}
                            onLoad={() => {
                              console.log('Profile picture loaded successfully:', profilePic);
                              if (avatarError) setAvatarError(false);
                            }}
                          />
                        );
                      }
                      return <User className="h-5 w-5 text-gray-600" />;
                    })()}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-gray-400 hover:text-gray-500"
                    title={t('navigation.logout')}
                    data-testid="logout-button"
                    aria-label="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
      
      {/* WhatsApp Bot Launcher */}
      <WhatsAppBotLauncher />
    </div>
  )
}

export default Layout
